// TodosPage — /todos route: came-of-age to-do queue (§4).
//
// Lists GET /api/todos (minors who have reached adulthood and need an adoption
// addendum). Each row offers "Start adoption addendum" → POST
// /api/todos/:id/start-addendum → navigate to the prefilled addendum draft.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/Toast';
import { ApiError } from '@/lib/api';
import { useTodos, startAddendum, todoMinorName, type TodoItem } from '@/hooks/useLeaseLifecycle';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

export function TodosPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { todos, loading, error, reload } = useTodos();
  // Track in-flight starts as a Set so we can disable ALL "Start adoption
  // addendum" buttons while any one is in flight — preventing a second start
  // (which would create a duplicate / orphan addendum draft) before the first
  // navigates away.
  const [starting, setStarting] = useState<Set<string>>(new Set());
  const anyStarting = starting.size > 0;

  const handleStart = async (todo: TodoItem) => {
    if (anyStarting) return;
    setStarting((prev) => new Set(prev).add(todo.todoId));
    try {
      const res = await startAddendum(todo.todoId);
      // Navigate to the prefilled addendum draft. The API returns the group it
      // belongs to (fall back to the todo's groupId); the lease editor route is
      // keyed by groupId. Carry the prefilled addendum payload in navigation
      // state so ConfigurePage can surface the "Send adoption addendum" action.
      const groupId = res.addendum?.groupId ?? todo.groupId;
      if (groupId) {
        navigate(`/applications/${encodeURIComponent(groupId)}/lease`, {
          state: { startAddendum: res },
        });
      } else {
        toast.success('Adoption addendum started.');
        await reload();
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? `Could not start addendum (HTTP ${err.status})`)
          : err instanceof Error
            ? err.message
            : 'Could not start addendum';
      toast.error(msg, { title: 'Start addendum failed' });
    } finally {
      setStarting((prev) => {
        const next = new Set(prev);
        next.delete(todo.todoId);
        return next;
      });
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-7)',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
            Came-of-age to-dos
          </h1>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
            Minors on existing tenancies who have reached adulthood and need an adoption addendum added to the lease.
          </p>
        </div>
        <button
          type="button"
          className="btn"
          data-variant="secondary"
          data-size="sm"
          onClick={() => void reload()}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {loading && <p style={{ color: 'var(--fg-muted)' }}>Loading…</p>}
      {error && (
        <div className="alert" data-tone="danger" role="alert">
          <div className="alert-body">
            <div className="alert-title">Couldn't load the to-do queue.</div>
            <div style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)' }}>{error}</div>
          </div>
        </div>
      )}

      {!loading && !error && todos.length === 0 && (
        <div className="empty">
          <div className="empty-glyph" aria-hidden="true">✓</div>
          <p className="empty-title">All caught up</p>
          <p className="empty-body">No minors have come of age that need an adoption addendum right now.</p>
        </div>
      )}

      {!loading && !error && todos.length > 0 && (
        <div className="table-wrap">
          <table className="table" data-zebra="true">
            <thead>
              <tr>
                <th scope="col">Resident (came of age)</th>
                <th scope="col">Came-of-age date</th>
                <th scope="col" className="col-action" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {todos.map((t) => (
                <tr key={t.todoId}>
                  <td style={{ fontWeight: 'var(--weight-medium)' }}>{todoMinorName(t)}</td>
                  {/* No tenancy column: the record carries only the group id, and an
                      id is not something anyone can use (Miles, 2026-08-29). The
                      action opens the addendum for that household. */}
                  <td style={{ color: 'var(--fg-muted)' }}>{formatDate(t.minor.turned18At)}</td>
                  <td className="col-action">
                    <button
                      type="button"
                      className="btn"
                      data-size="sm"
                      onClick={() => void handleStart(t)}
                      disabled={anyStarting}
                    >
                      {starting.has(t.todoId) ? 'Starting…' : 'Start adoption addendum'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
